import type { FC, ReactNode } from 'react';
import { ArrowUp, Paperclip, X } from 'lucide-react';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Tooltip } from '@cotera/client/app/components/ui';
import { cn } from '@cotera/client/v0/lib/utils';
import type { QueuedMessage } from '../models/message-queue.model';

interface QueuedMessagesProps {
  items: readonly QueuedMessage[];
  /** Cancel the in-flight run and send this message immediately. */
  onSubmitNow: (item: QueuedMessage) => void;
  /** Drop the message from the queue without sending it. */
  onRemove: (id: string) => void;
}

function attachmentLabel(count: number): string {
  return count === 1 ? '1 attachment' : `${count} attachments`;
}

/**
 * Equal-size icon button for a queued row's actions. `tone` tints the hover
 * state so the two actions read differently: the primary send and the
 * destructive remove. Both stay muted at rest to keep the row calm.
 */
const QueueRowAction: FC<{
  icon: ReactNode;
  tooltip: string;
  label: string;
  tone: 'primary' | 'destructive';
  onClick: () => void;
}> = ({ icon, tooltip, label, tone, onClick }) => (
  <Tooltip side="top" tooltipContent={tooltip}>
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className={cn(
        'size-6 shrink-0 text-muted-foreground',
        tone === 'primary'
          ? 'hover:bg-primary/10 hover:text-primary'
          : 'hover:bg-destructive/10 hover:text-destructive'
      )}
      onClick={onClick}
      aria-label={label}
    >
      {icon}
    </Button>
  </Tooltip>
);

/**
 * Shows messages the user queued while a run is in progress. Each waits its turn
 * (the queue drains in order as runs finish); the ↑ button cancels the current
 * run and sends that message immediately.
 *
 * Rendered as standalone pills (no outer panel) so the stack reads at the same
 * width as the composer beneath it, and scrolls within a capped height so a long
 * queue never pushes the composer off screen.
 */
export const QueuedMessages: FC<QueuedMessagesProps> = ({
  items,
  onSubmitNow,
  onRemove,
}) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="mb-2 flex flex-col gap-1 px-3">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="font-medium">{items.length} queued</span>
        <span>sends when this finishes</span>
      </div>
      <div className="flex max-h-44 flex-col gap-1 overflow-y-auto">
        {items.map((item, index) => {
          const hasPreview = item.preview.trim().length > 0;
          return (
            <div
              key={item.id}
              // bg-coco-accent: the warm brand surface used across the coco v1 UI
              // (skeletons, placeholders) rather than a neutral gray.
              className="flex items-center gap-2 rounded-md bg-coco-accent py-1 pl-3 pr-1.5"
            >
              <span className="shrink-0 text-xs font-medium text-muted-foreground tabular-nums">
                {index + 1}
              </span>
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span
                  className={cn(
                    'truncate text-sm',
                    hasPreview
                      ? 'text-foreground'
                      : 'italic text-muted-foreground'
                  )}
                >
                  {hasPreview ? item.preview : 'Attachment'}
                </span>
                {item.attachmentCount > 0 && (
                  <span className="flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
                    <Paperclip className="size-3" />
                    {attachmentLabel(item.attachmentCount)}
                  </span>
                )}
              </div>
              <QueueRowAction
                icon={<ArrowUp className="size-4" />}
                tooltip="Send now (stops current)"
                label="Send this message now and stop the current response"
                tone="primary"
                onClick={() => onSubmitNow(item)}
              />
              <QueueRowAction
                icon={<X className="size-3.5" />}
                tooltip="Remove from queue"
                label="Remove queued message"
                tone="destructive"
                onClick={() => onRemove(item.id)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
